/**
 * FAQSection — Expandable FAQ accordion
 * Design: Light gray background, navy text, green accents
 */

import React, { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";

const faqs = [
  {
    q: "How quickly can I get funded?",
    a: "Funding timelines vary by product. Working capital and lines of credit can fund in 24-48 hours. Equipment financing typically takes 3-7 days. SBA loans require 2-4 weeks due to the government guarantee process. Your funding specialist will give you an exact timeline based on your situation.",
  },
  {
    q: "What credit score do I need to qualify?",
    a: "Minimum credit requirements vary by product. Most options require a 600+ personal credit score. However, we have solutions for scores as low as 550, and our Business Credit Builder program accepts all credit profiles. We'll match you with the best option for your situation.",
  },
  {
    q: "How much can I borrow?",
    a: "Funding amounts range from $5,000 to $5 million+ depending on the product, your business financials, and creditworthiness. SBA loans offer the highest amounts (up to $5M), while working capital lines typically max out around $500K. We'll help you find the right amount.",
  },
  {
    q: "Are there any upfront fees?",
    a: "No upfront fees. We operate on a success-based model — we only earn a fee when you successfully receive funding. There are no application fees, no processing fees, and no hidden charges. What you see is what you pay.",
  },
  {
    q: "Will applying affect my credit score?",
    a: "Our initial prequalification uses a soft credit pull, which does not affect your credit score. A hard pull only occurs when you proceed with a full application to a specific lender. We'll always inform you before any hard inquiry is made.",
  },
  {
    q: "What documents do I need to apply?",
    a: "Basic requirements include: 3-6 months of business bank statements, most recent business and personal tax returns, a valid government ID, and basic business information. SBA loans require additional documentation. Your funding specialist will provide a complete checklist.",
  },
];

export default function FAQSection() {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <section id="faq" data-testid="faq-section" className="py-20 md:py-28 bg-[#f8fafc]">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-14 fade-up">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="h-px w-8 bg-[#48d480]" />
            <span className="section-label">FAQs</span>
            <div className="h-px w-8 bg-[#48d480]" />
          </div>
          <h2
            style={{ fontFamily: "'Montserrat', sans-serif" }}
            className="text-3xl md:text-4xl font-bold text-[#0f2d52]"
          >
            Frequently Asked Questions
          </h2>
          <p
            style={{ fontFamily: "'Inter', sans-serif" }}
            className="text-gray-500 mt-3 max-w-xl mx-auto"
          >
            Get answers to common questions about our funding process, requirements, and timeline.
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-3xl mx-auto space-y-3 fade-up">
          {faqs.map((faq, idx) => (
            <button
              key={idx}
              onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
              className={`w-full bg-white rounded-xl border text-left transition-all duration-300 ${
                openIdx === idx
                  ? "border-[#48d480] shadow-md"
                  : "border-gray-200 hover:border-[#48d480]/50"
              }`}
            >
              {/* Question */}
              <div className="flex items-center justify-between p-5">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                      openIdx === idx ? "bg-[#48d480]" : "bg-[#0f2d52]/10"
                    }`}
                  >
                    <HelpCircle
                      size={16}
                      className={openIdx === idx ? "text-[#0f2d52]" : "text-[#0f2d52]/70"}
                    />
                  </div>
                  <span
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    className="text-[#0f2d52] font-semibold text-sm md:text-base"
                  >
                    {faq.q}
                  </span>
                </div>
                <ChevronDown
                  size={18}
                  className={`text-[#48d480] flex-shrink-0 transition-transform duration-300 ${
                    openIdx === idx ? "rotate-180" : ""
                  }`}
                />
              </div>
              {/* Answer */}
              {openIdx === idx && (
                <div className="px-5 pb-5">
                  <div className="border-t border-gray-100 pt-4 pl-11">
                    <p
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      className="text-gray-600 text-sm leading-relaxed"
                    >
                      {faq.a}
                    </p>
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* More Questions CTA */}
        <div className="text-center mt-12 fade-up">
          <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-sm mb-4">
            Still have questions? We're here to help.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 bg-white border-2 border-[#0f2d52] text-[#0f2d52] font-bold px-5 py-2.5 rounded-lg hover:bg-[#0f2d52] hover:text-white transition-colors"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              View All FAQs
            </Link>
            <Link
              to="/contact"
              className="btn-primary flex items-center gap-2"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
